import { HttpError } from './errors';

interface Credentials {
  email: string;
  password: string;
  twoFactorAuthChallengeCode?: string;
}

export enum AuthErrorTypes {
  MAX_LOGIN_ATTEMPTS = 'MAX_LOGIN_ATTEMPTS',
  TWO_FACTOR_AUTH_CHALLENGE_REQUIRED = 'TWO_FACTOR_AUTH_CHALLENGE_REQUIRED',
  TWO_FACTOR_AUTH_CHALLENGE_INVALID = 'TWO_FACTOR_AUTH_CHALLENGE_INVALID',
}

export const CLIENT_ID = 'game';

export async function login(credentials: Credentials) {
  const res = await fetch('/api/auth/session', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      providerType: 'password',
      ...credentials,
      clientId: CLIENT_ID,
    }),
  });
  // This call sets httponly cookie there's nothing we can do here just check http status code, it returns 401 if credentials are wrong
  if (res.status === 401) {
    const body = await res.json().catch(() => null);
    if (
      typeof body.message === 'string' &&
      Object.values(AuthErrorTypes).includes(body.message)
    ) {
      return body.message as AuthErrorTypes;
    } else {
      return false;
    }
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new HttpError(res.status, body);
  }
  return true;
}

export async function loginWithMagicLink(token: string) {
  const res = await fetch('/api/auth/magic-link', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      token,
      clientId: CLIENT_ID,
    }),
  });

  // This call sets httponly cookie there's nothing we can do here just check http status code, it returns 401 if token is invalid
  if (res.status === 401) {
    return false;
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new HttpError(res.status, body);
  }
  return true;
}

export async function loginWithOicd(
  idToken: string,
  accessToken: string | null,
  ssoConfig: string,
  nonce: string | null,
) {
  const res = await fetch('/api/auth/session', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      providerType: 'oidc',
      idToken,
      accessToken: accessToken ?? undefined,
      configName: ssoConfig,
      nonce: nonce ?? undefined,
      clientId: CLIENT_ID,
    }),
  });
  // This call sets httponly cookie there's nothing we can do here just check http status code, it returns 401 if token is valid, but this user isn't in the system
  if (res.status === 401) {
    return false;
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new HttpError(res.status, body);
  }
  return true;
}

export interface SsoLogoutResponse {
  config: string;
  endSsoSessionEndpoint: string;
  idToken?: string;
}

export async function logout(): Promise<SsoLogoutResponse | null> {
  const res = await fetch('/api/auth/session', {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new HttpError(res.status, body);
  }
  if (res.status === 204) {
    return null;
  }
  return res.json();
}

export interface SsoConfig {
  name: string;
  type: 'oidc';
  grantType: 'implicit' | 'adfs-implicit' | 'auth_code';
  authEndpoint: string;
  clientId: string;
  additionalScopes: string | null;
  tokenEndpoint: string | null;
}

interface LoginUserConfig {
  companyLayout?: string;
}

type SsoLoginProviderResponse = { providerType: 'sso' } & SsoConfig &
  LoginUserConfig;

export type LoginProviderResponse =
  | ({ providerType: 'password' } & LoginUserConfig)
  | ({ providerType: 'magic-link' } & LoginUserConfig)
  | SsoLoginProviderResponse;
/**
 * Get login provider, ssoConfig or email is required param
 * @param ssoConfig string
 * @param email string
 * @returns
 */
export async function getLoginProvider(
  ssoConfig?: string,
  email?: string,
): Promise<LoginProviderResponse> {
  if (!ssoConfig && !email) {
    throw new Error('ssoConfig or email must be provided');
  }

  const query = new URLSearchParams();
  if (ssoConfig) {
    query.append('ssoConfig', ssoConfig);
  } else if (email) {
    query.append('email', email);
  }

  const res = await fetch(`/api/auth/login_provider?${query.toString()}`);
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new HttpError(res.status, body);
  }
  return res.json();
}

export function setSessionStorageForRedirectingToAuthUrl(
  provider: SsoLoginProviderResponse,
  successNavigate?: string,
) {
  sessionStorage.setItem('sso.grantType', provider.grantType);
  sessionStorage.setItem('sso.successNavigate', successNavigate || '');
}
